import { motion } from 'framer-motion'
import { useState } from 'react'

interface Product {
  id: string
  name: string
  category: string
  description: string
  image: string
  hoverImage?: string
  href: string
  badge?: string
}

const defaultProducts: Product[] = [
  {
    id: 'speedy-beauty-sun',
    name: 'Speedy Beauty Sun',
    category: 'Solari — Novità',
    description: 'La nuova proposta solare Nashi Argan: bellezza in pochi gesti, anche al sole.',
    image: '/images/products/speedy-beauty-sun.png',
    href: '#speedy-beauty-sun',
    badge: 'Novità',
  },
  {
    id: 'oil-bloom',
    name: 'Oil Bloom Fragrance',
    category: 'Capelli — Spring Collection',
    description: 'Olio luminoso con la fragranza Bloom, fresca e floreale.',
    image: '/images/products/oil-bloom-fragrance-hover.png',
    href: '#oil-bloom',
    badge: 'Spring Collection',
  },
  {
    id: 'love-hair-mist',
    name: 'LOVE Hair Mist',
    category: 'Fragranze — Capelli',
    description: 'Mist profumante per chiome luminose con la fragranza iconica Nashi Argan.',
    image: '/images/products/love-hair-mist-hover.jpg',
    href: '#love-hair-mist',
  },
]

interface ProductShowcaseProps {
  eyebrow?: string
  title?: string
  titleItalic?: string
  intro?: string
  products?: Product[]
  viewAllLabel?: string
  viewAllHref?: string
}

export function ProductShowcase({
  eyebrow,
  title = 'Scopri tutti i prodotti',
  titleItalic = 'Nashi Argan',
  intro = 'Prenditi cura dei tuoi capelli con tutta l\'efficacia dei classici prodotti Nashi Argan, pensati per esaltare, nutrire e ammorbidire qualsiasi tipo di capello. Abbina i prodotti capelli a quelli dedicati a viso e corpo: tutti contraddistinti dalla qualità e dall\'inebriante fragranza Nashi Argan.',
  products = defaultProducts,
  viewAllLabel = 'Vedi tutti i prodotti',
  viewAllHref = '#prodotti',
}: ProductShowcaseProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  return (
    <section className="relative bg-cream py-24 md:py-36" id="prodotti">
      <div className="nashi-container">
        {/* Header — editorial split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-24 items-end">
          <div className="lg:col-span-7">
            {eyebrow && (
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1, delay: 0.1, ease: [0.25, 1, 0.5, 1] }}
                className="nashi-eyebrow"
              >
                {eyebrow}
              </motion.span>
            )}

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.02em',
              }}
            >
              <span className="block">{title}</span>
              <span className="block italic font-light text-argan-deep">
                {titleItalic}
              </span>
            </motion.h2>
          </div>

          {intro && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-5 lg:pl-6"
            >
              <p className="text-bruno-soft text-base md:text-lg leading-relaxed">
                {intro}
              </p>
            </motion.div>
          )}
        </div>

        {/* Product grid — asymmetric, middle item taller */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 lg:gap-x-10 gap-y-16 md:gap-y-0">
          {products.map((product, idx) => (
            <motion.a
              key={product.id}
              href={product.href}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 1,
                delay: idx * 0.12,
                ease: [0.25, 1, 0.5, 1],
              }}
              onMouseEnter={() => setHoveredId(product.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`group block relative ${
                idx === 1 ? 'md:mt-12' : idx === 2 ? 'md:mt-24' : ''
              }`}
            >
              {/* Image */}
              <div className="relative overflow-hidden bg-cream-soft aspect-[3/4]">
                <img
                  src={product.image}
                  alt={product.name}
                  loading="lazy"
                  className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out ${
                    hoveredId === product.id ? 'scale-110' : 'scale-100'
                  }`}
                />
                {/* Tonal hover overlay */}
                <div
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    hoveredId === product.id ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(59,49,46,0) 50%, rgba(59,49,46,0.55) 100%)',
                  }}
                />

                {/* Badge */}
                {product.badge && (
                  <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-cream/85 backdrop-blur-sm py-1.5 px-3 text-[9px] uppercase tracking-[0.24em] text-bruno">
                    <span className="w-1 h-1 bg-argan-deep" />
                    {product.badge}
                  </span>
                )}

                {/* Numeral */}
                <span className="absolute top-4 right-4 font-display italic text-cream/80 text-2xl md:text-3xl mix-blend-difference">
                  0{idx + 1}
                </span>

                {/* Reveal CTA on hover */}
                <span
                  className={`absolute bottom-6 left-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-cream transition-all duration-700 ${
                    hoveredId === product.id
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-3'
                  }`}
                >
                  Scopri di più
                  <span className="block w-6 h-px bg-cream" />
                </span>
              </div>

              {/* Text */}
              <div className="mt-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-bruno-muted">
                    {product.category}
                  </p>
                  <h3 className="mt-2 font-display text-bruno text-2xl md:text-[28px] leading-tight">
                    {product.name}
                  </h3>
                  <p className="mt-3 text-sm text-bruno-soft leading-relaxed max-w-[28ch]">
                    {product.description}
                  </p>
                </div>
                <span
                  className={`mt-1 shrink-0 font-display italic text-argan-deep text-xl transition-transform duration-500 ${
                    hoveredId === product.id ? '-translate-y-1 translate-x-1' : ''
                  }`}
                >
                  ↗
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* View all CTA */}
        {viewAllLabel && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="mt-20 md:mt-28 flex justify-center"
          >
            <a
              href={viewAllHref}
              className="group inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.24em] text-bruno pb-2 border-b border-bruno/40 hover:border-argan-deep hover:text-argan-deep transition-colors"
            >
              {viewAllLabel}
              <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5">
                →
              </span>
            </a>
          </motion.div>
        )}
      </div>
    </section>
  )
}
